import IconDoubleChevronLeft from '@apollo/icons/default/IconDoubleChevronLeft.svg';
import IconDoubleChevronRight from '@apollo/icons/default/IconDoubleChevronRight.svg';
import IconSupport from '@apollo/icons/default/IconSupport.svg';
import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect } from 'react';

import { useWindowSize } from 'src/hooks/useWindowSize';

import { HelpAndSupportMenu } from './components/HelpAndSupportMenu';
import { SidebarHeader } from './components/SidebarHeader';
import { useSidebarControls } from './useSidebarControls';

export const Sidebar = ({ children }: { children?: React.ReactNode }) => {
  const { isExpanded, shouldBeExpanded, isToggleFooterVisible } =
    useSidebarControls();
  const [windowWidth = 0] = useWindowSize();

  useEffect(() => {
    if (isExpanded && windowWidth <= 768) {
      shouldBeExpanded(false);
    }
  }, [isExpanded, shouldBeExpanded, windowWidth]);

  const toggleSidebar = () => shouldBeExpanded((prev) => !prev);

  return (
    <aside
      className={classnames(
        'flex flex-col justify-start border-l border-primary bg-primary text-base font-semibold text-secondary sm:h-full sm:justify-center sm:overflow-hidden',
        isExpanded ? 'sm:w-72 sm:min-w-72' : 'sm:w-20 sm:min-w-20',
      )}
    >
      <SidebarHeader isExpanded={isExpanded} />
      <div className="hidden flex-1 flex-col justify-between sm:flex">
        <div className="flex-auto flex-col">{isExpanded ? children : null}</div>
        <div
          className={classnames(
            'hidden flex-row items-center p-4 sm:flex',
            isExpanded ? 'justify-between' : 'justify-center',
          )}
        >
          <span
            className={classnames(
              'inline-flex flex-row items-center gap-2 text-base font-normal',
              { 'hidden w-0': !isExpanded },
            )}
          >
            <IconSupport />
            Help & Support
          </span>
          <HelpAndSupportMenu />
        </div>

        {isToggleFooterVisible && windowWidth > 768 ? (
          <div
            className={classnames(
              'flex border-t border-primary p-4',
              isExpanded ? 'justify-start' : 'justify-center',
            )}
          >
            <Button
              aria-label={`Click to ${
                isExpanded ? 'collapse' : 'expand'
              } sidebar`}
              variant="hidden"
              size="sm"
              onClick={toggleSidebar}
              leftIcon={isExpanded ? <IconDoubleChevronLeft /> : undefined}
            >
              {isExpanded ? 'COLLAPSE' : <IconDoubleChevronRight />}
            </Button>
          </div>
        ) : null}
      </div>
    </aside>
  );
};
